import { z } from 'zod';
import { IsoDateTime, Pence, SignedPence, Uuid } from '../primitives.js';

/**
 * Payments core contract (Phase 2). Payments area OWNS these enums + the Payment/PaymentSplit read
 * DTOs (CONTRACT amendment B); receipts/reports/webhooks import — never redeclare.
 * Money is integer pence. Entity/response schemas carry `.meta({ id })`; query/param schemas must
 * NOT (the @fastify/swagger gotcha).
 */

// ── Enums ────────────────────────────────────────────────────────────────────
/** The locked state machine — 1:1 with Stripe PaymentIntent statuses plus our refund terminals. */
export const PaymentStatus = z.enum([
  'requires_payment_method',
  'requires_action',
  'processing',
  'succeeded',
  'failed',
  'canceled',
  'refunded',
  'partially_refunded',
]);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

export const PaymentKind = z.enum(['deposit', 'balance', 'no_show_fee', 'retail', 'full']);
export type PaymentKind = z.infer<typeof PaymentKind>;

export const PaymentMethod = z.enum(['online', 'terminal']);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const ConnectedAccountStatus = z.enum(['pending', 'restricted', 'enabled', 'disabled']);
export type ConnectedAccountStatus = z.infer<typeof ConnectedAccountStatus>;

export const RefundReason = z.enum([
  'requested_by_customer',
  'duplicate',
  'fraudulent',
  'no_show_waived',
  'other',
]);
export type RefundReason = z.infer<typeof RefundReason>;

/** Local refund lifecycle (distinct from the payment state machine). */
export const RefundStatus = z.enum(['pending', 'succeeded', 'failed', 'canceled']);
export type RefundStatus = z.infer<typeof RefundStatus>;

// ── Connected account ──────────────────────────────────────────────────────────
export const ConnectedAccount = z
  .object({
    id: Uuid,
    salonId: Uuid,
    stripeAccountId: z.string(),
    status: ConnectedAccountStatus,
    chargesEnabled: z.boolean(),
    payoutsEnabled: z.boolean(),
    detailsSubmitted: z.boolean(),
    requirementsDue: z.array(z.string()),
    defaultCurrency: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'ConnectedAccount' });
export type ConnectedAccount = z.infer<typeof ConnectedAccount>;

export const AccountLink = z
  .object({
    url: z.url(),
    expiresAt: IsoDateTime,
    accountStatus: ConnectedAccountStatus,
  })
  .meta({ id: 'AccountLink' });
export type AccountLink = z.infer<typeof AccountLink>;

export const CreateAccountLink = z
  .object({
    returnPath: z.string().optional(),
    refreshPath: z.string().optional(),
  })
  .meta({ id: 'CreateAccountLink' });
export type CreateAccountLink = z.infer<typeof CreateAccountLink>;

// ── Payment splits ─────────────────────────────────────────────────────────────
export const PaymentSplit = z
  .object({
    id: Uuid,
    paymentId: Uuid,
    renterId: Uuid.nullable(),
    staffId: Uuid,
    serviceId: Uuid.nullable(),
    amountPence: Pence,
    platformFeePence: Pence,
    description: z.string().nullable(),
  })
  .meta({ id: 'PaymentSplit' });
export type PaymentSplit = z.infer<typeof PaymentSplit>;

// ── Payment (read DTO) ───────────────────────────────────────────────────────
/** Never exposes client_secret in list/read — that is returned ONLY in PaymentIntentCreated. */
export const Payment = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    appointmentId: Uuid.nullable(),
    clientId: Uuid.nullable(),
    staffId: Uuid.nullable(),
    kind: PaymentKind,
    method: PaymentMethod,
    status: PaymentStatus,
    amountPence: Pence,
    platformFeePence: Pence,
    stripeFeePence: Pence,
    amountRefundedPence: Pence,
    currency: z.literal('GBP'),
    description: z.string().nullable(),
    failureMessage: z.string().nullable(),
    splits: z.array(PaymentSplit),
    succeededAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Payment' });
export type Payment = z.infer<typeof Payment>;

export const PaymentIntentCreated = z
  .object({
    payment: Payment,
    clientSecret: z
      .string()
      .describe(
        'Stripe PaymentIntent client_secret — confirm client-side for the optimistic token (Overview §3.2)',
      ),
    publishableContext: z.object({ accountId: z.string() }),
  })
  .meta({ id: 'PaymentIntentCreated' });
export type PaymentIntentCreated = z.infer<typeof PaymentIntentCreated>;

// ── Requests ─────────────────────────────────────────────────────────────────
export const CreatePaymentIntent = z
  .object({
    appointmentId: Uuid.nullish(),
    clientId: Uuid.nullish(),
    amountPence: Pence,
    kind: PaymentKind.default('balance'),
    method: PaymentMethod.default('online'),
    terminalReaderId: z.string().optional(),
    description: z.string().max(300).optional(),
    splits: z
      .array(z.object({ staffId: Uuid, serviceId: Uuid.nullish(), amountPence: Pence }))
      .optional(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'CreatePaymentIntent' });
export type CreatePaymentIntent = z.infer<typeof CreatePaymentIntent>;

export const CreateDeposit = z
  .object({
    appointmentId: Uuid,
    amountPence: Pence.optional().describe('defaults to salon deposit policy / service deposit'),
    method: PaymentMethod.default('online'),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'CreateDeposit' });
export type CreateDeposit = z.infer<typeof CreateDeposit>;

export const CreateNoShowFee = z
  .object({
    appointmentId: Uuid,
    amountPence: Pence.optional(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'CreateNoShowFee' });
export type CreateNoShowFee = z.infer<typeof CreateNoShowFee>;

export const CreateRefund = z
  .object({
    amountPence: Pence.optional().describe('omit for full remaining refund'),
    reason: RefundReason.default('requested_by_customer'),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'CreateRefund' });
export type CreateRefund = z.infer<typeof CreateRefund>;

// ── Refund (read DTO) ──────────────────────────────────────────────────────────
export const Refund = z
  .object({
    id: Uuid,
    salonId: Uuid,
    paymentId: Uuid,
    amountPence: Pence,
    reason: RefundReason,
    status: RefundStatus,
    failureReason: z.string().nullable(),
    createdByStaffId: Uuid.nullable(),
    succeededAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Refund' });
export type Refund = z.infer<typeof Refund>;

// ── Fee composition (transparency, Overview §6.10) ──────────────────────────────
export const FeeComposition = z
  .object({
    amountPence: Pence,
    platformFeePence: Pence,
    platformFeeBps: z.int(),
    stripeFeePence: Pence,
    stripePctBps: z.int(),
    stripeFixedPence: Pence,
    totalFeePence: Pence,
    effectiveRateBps: z.int().describe('total fee as bps of amount; target <= 200'),
    netToSalonPence: SignedPence,
    explanation: z
      .string()
      .describe('plain-language line, e.g. We charge 1.95%; Stripe charges 1.5% + 20p'),
  })
  .meta({ id: 'FeeComposition' });
export type FeeComposition = z.infer<typeof FeeComposition>;

// ── Query schemas (NO .meta — query/param gotcha) ───────────────────────────────
export const FeePreviewQuery = z.object({
  amountPence: z.coerce.number().int().nonnegative(),
  method: PaymentMethod.default('online'),
});
export type FeePreviewQuery = z.infer<typeof FeePreviewQuery>;

export const ListPaymentsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  status: PaymentStatus.optional(),
  appointmentId: Uuid.optional(),
  from: IsoDateTime.optional(),
  to: IsoDateTime.optional(),
});
export type ListPaymentsQuery = z.infer<typeof ListPaymentsQuery>;
